/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, createContext, ReactNode } from "react"
import { Transition } from "@headlessui/react"
import Button from "@common/buttons/button"
import ButtonFormWithSubmit from "@common/buttons/buttonFormWithSubmit"

interface Steps {
  name?: string
  description?: string
  status?: string
  actionText?: string
  disabled?: boolean
}

interface WorkFlowProps {
  children: ReactNode
  steps: Steps[]
  onCurrentIdxChange?: (index: number) => void
  startStepIdx?: number
}

interface WorkFlowStepsProps {
  steps: Steps[]
  children: (props: { step: Steps; index: number }) => ReactNode
}

interface WorkFlowTabProps {
  children: (props: {
    isSelected: boolean
    handleSelect: (stepIdx: number) => void
    isCompleted: boolean
    isLast: boolean
  }) => ReactNode
  step: Steps
}

interface WorkFlowPanelProps {
  children: (isActive: boolean) => ReactNode
  stepIdx: number
  animate?: boolean
}

interface WorkFlowNextProps {
  children: (props: { moveForward: () => void }) => ReactNode
}

interface WorkFlowBackProps {
  children: (props: { moveBack: () => void }) => ReactNode
}

interface WorkFlowFooterProps {
  handleNextClick: (moveForward: () => void) => void
  disabled?: boolean
  submitFunction?: () => Promise<any>
  onSubmitSuccess?: (data: any, moveForward: () => void) => void
  onSubmitFail?: (message?: string[], data?: any) => void
  noSubmitMode?: boolean
  nextButtonText?: string
}

const WorkFlowContext = createContext<any>(null)

export default function WorkFlow({
  children,
  steps,
  onCurrentIdxChange,
  startStepIdx,
}: WorkFlowProps) {
  const [currentStepIdx, setCurrentStepIdx] = useState(startStepIdx || 0)
  const [previousIndex, setPreviousIndex] = useState(0)

  useEffect(() => {
    if (startStepIdx) setCurrentStepIdx(startStepIdx)
  }, [startStepIdx])

  useEffect(() => {
    if (onCurrentIdxChange) onCurrentIdxChange(currentStepIdx)
  }, [currentStepIdx])

  const handleNext = () => {
    if (currentStepIdx != steps.length - 1) {
      setPreviousIndex(currentStepIdx)
      setCurrentStepIdx(currentStepIdx + 1)
    }
  }

  const handleBack = () => {
    if (currentStepIdx != 0) {
      setPreviousIndex(currentStepIdx)
      setCurrentStepIdx(currentStepIdx - 1)
    }
  }

  const handleSelect = (stepIdx: number) => {
    if (stepIdx >= currentStepIdx || steps[stepIdx]?.disabled) return
    else {
      setPreviousIndex(currentStepIdx)
      setCurrentStepIdx(stepIdx)
    }
  }

  return (
    <WorkFlowContext.Provider
      value={{
        steps,
        currentStepIdx,
        handleNext,
        handleBack,
        handleSelect,
        previousIndex,
      }}
    >
      {children}
    </WorkFlowContext.Provider>
  )
}

export function WorkFlowSteps({ steps, children }: WorkFlowStepsProps) {
  return (
    <>
      {steps.map((step, index) => {
        return children({ step, index })
      })}
    </>
  )
}

export function WorkFlowTab({ children, step }: WorkFlowTabProps) {
  return (
    <WorkFlowContext.Consumer>
      {({ steps, currentStepIdx, handleSelect }) => {
        const stepIndex = steps.indexOf(step)
        const isSelected = currentStepIdx === stepIndex
        const isCompleted = currentStepIdx > stepIndex
        const isLast = stepIndex == steps.length - 1
        return children({
          isSelected,
          handleSelect,
          isCompleted,
          isLast,
        })
      }}
    </WorkFlowContext.Consumer>
  )
}

export const WorkFlowPanel = ({
  children,
  stepIdx,
  animate = false,
}: WorkFlowPanelProps) => (
  <WorkFlowContext.Consumer>
    {({ currentStepIdx, previousIndex }) => {
      const toLeft = currentStepIdx < previousIndex
      if (!animate) return children(currentStepIdx === stepIdx)
      return (
        <Transition
          show={currentStepIdx == stepIdx}
          enter="transition-all duration-300"
          enterFrom={
            toLeft
              ? "transform -translate-x-full opacity-0"
              : "transform translate-x-full opacity-0"
          }
          enterTo={"transform translate-x-0 opacity-100"}
          leave="transition-all duration-300"
          leaveFrom={"transform translate-x-0"}
          leaveTo={
            toLeft
              ? "transform -translate-x-full opacity-0"
              : "transform translate-x-full"
          }
        >
          {children(currentStepIdx === stepIdx)}
        </Transition>
      )
    }}
  </WorkFlowContext.Consumer>
)

export function WorkFlowNext({ children }: WorkFlowNextProps) {
  return (
    <WorkFlowContext.Consumer>
      {({ handleNext }) => children({ moveForward: handleNext })}
    </WorkFlowContext.Consumer>
  )
}

export function WorkFlowBack({ children }: WorkFlowBackProps) {
  return (
    <WorkFlowContext.Consumer>
      {({ currentStepIdx, handleBack }) =>
        currentStepIdx == 0 ? null : children({ moveBack: handleBack })
      }
    </WorkFlowContext.Consumer>
  )
}

export function WorkFlowFooter({
  handleNextClick,
  disabled,
  submitFunction,
  onSubmitFail,
  onSubmitSuccess,
  noSubmitMode,
  nextButtonText = "Next",
}: WorkFlowFooterProps) {
  const handleSubmit = (data: any, moveForward: () => void): void => {
    if (onSubmitSuccess) onSubmitSuccess(data, moveForward)
    else moveForward()
  }
  return (
    <div className="flex gap-2 items-center justify-end">
      <WorkFlowBack>
        {({ moveBack }) => (
          <Button onClick={moveBack} variant={"outline"}>
            Back
          </Button>
        )}
      </WorkFlowBack>

      <WorkFlowNext>
        {({ moveForward }) => {
          return (
            <ButtonFormWithSubmit
              submitFunction={submitFunction}
              onSubmitSuccess={(data: any) => handleSubmit(data, moveForward)}
              onSubmitFail={onSubmitFail}
              noSubmitMode={noSubmitMode}
              disabled={disabled}
              onSimpleClick={() => handleNextClick(moveForward)}
            >
              {nextButtonText}
            </ButtonFormWithSubmit>
          )
        }}
      </WorkFlowNext>
    </div>
  )
}
